import React, { useState } from 'react';
import { X, ChevronDown, ArrowRight, BarChart3, Eye, Settings } from 'lucide-react';
import { Company } from '../../services/api';
import { getStaticFileUrl } from '@/utils/backendUrl';
import LogoString from '../../assets/images/logo_string.svg';
import TransactionProcessingModal from './TransactionProcessingModal';
import TransactionCompleteModal from './TransactionCompleteModal';
import { useSendMoney } from '../../contexts/SendMoneyContext';
import { useWallet } from '@solana/wallet-adapter-react';

interface SendMoneyModalProps {
  availableCompanies: {company: Company, exchangeRate: number}[];
  currentCompany?: Company;
  isLoading?: boolean;
  onSubmit: (data: { receiverCompanyId: string; amount: string }) => void;
}

// Default company logo component
const DefaultCompanyLogo: React.FC<{ companyName: string, size: number }> = ({ companyName, size }) => {
  const initials = companyName
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
  
  return (
    <div 
      className="bg-blue-500 rounded flex items-center justify-center text-white text-sm font-semibold"
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      {initials}
    </div>
  );
};

// Company logo component
const CompanyLogo: React.FC<{ company: Company, size: number }> = ({ company, size }) => {
  if (company.logoUrl) {
    return (
      <img 
        src={getStaticFileUrl(company.logoUrl || '')} 
        alt={company.companyName}
        className="rounded object-cover"
        style={{ width: `${size}px`, height: `${size}px` }}
        width={size}
        height={size}
        onError={(e) => {
          // Fallback to default logo if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = `<div class="bg-blue-500 rounded flex items-center justify-center text-white text-sm font-semibold" style="width: ${size}px; height: ${size}px;">${company.companyName.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2)}</div>`;
          }
        }}
      />
    );
  }
  
  return <DefaultCompanyLogo companyName={company.companyName} size={size} />;
};

// Custom dropdown component
const CompanyDropdown: React.FC<{
  companies: Company[];
  selectedCompanyId: string;
  onSelect: (companyId: string) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ companies, selectedCompanyId, onSelect, placeholder, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between appearance-none rounded-md border-[1px] bg-[#182233] border-solid border-[#3C5475] px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
        disabled={disabled}
      >
        <div className="flex items-center space-x-3 text-[18px]">
          {selectedCompany ? (
            <>
              <CompanyLogo company={selectedCompany} size={40} />
              <span className="text-white">{selectedCompany.companyName}</span>
            </>
          ) : (
            <span className="text-[#567095]">{placeholder}</span>
          )}
        </div>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </button>
      
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-[#182233] border border-[#567095]/30 rounded-md shadow-lg max-h-60 overflow-auto">
          {companies.map((company) => (
            <button
              key={company.id}
              type="button"
              onClick={() => {
                onSelect(company.id);
                setIsOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-6 py-4 text-left hover:bg-[#567095]/20 text-[#567095] text-[22px]"
            >
              <CompanyLogo company={company} size={40} />
              <span>{company.companyName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SendMoneyModal: React.FC<SendMoneyModalProps> = ({
  availableCompanies,
  currentCompany,
  isLoading = false,
  onSubmit,
}) => {
  const { publicKey, signTransaction } = useWallet();
  const {
    isOpen,
    showProcessing: _showProcessing,
    showComplete,
    currentStep,
    formData,
    transactionData,
    transData,
    setCurrentStep,
    updateFormData,
    closeModal,
    handleProcessingComplete,
    handleNewSettlement,
    showProcessingModal,
  } = useSendMoney();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep === 1) {
      setCurrentStep(2);
    } else {
      onSubmit({...formData});
      showProcessingModal();
      // updateFormData({ receiverCompanyId: '', amount: '' });
      // setShowProcessing?.(true);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed space-y-[0px] inset-0 bg-black-200 bg-opacity-50 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
      <div className="relative flex flex-col justify-around mx-auto px-4 sm:px-[10%] pt-8 sm:pt-12 pb-4 sm:pb-8 w-full max-w-full sm:max-w-[90%] md:max-w-[80%] h-auto shadow-lg rounded-md bg-[#0E131A] my-auto">
        <div className="absolute top-4 sm:top-8 left-4 sm:left-8 sm:left-12">
          <img src={LogoString} alt="Logo" className="h-5 sm:h-[26px] w-auto" />
        </div>
        <button 
            onClick={closeModal} 
            className="text-gray-400 hover:text-gray-300 absolute top-3 right-3 sm:top-4 sm:right-4 z-10"
            disabled={isLoading}
        >
            <X className="h-6 w-6" />
        </button>
        <div className="flex flex-col justify-center items-center mb-4 sm:mb-6">
          <h3 className="text-2xl sm:text-3xl md:text-[44px] font-medium text-white text-center">Initiate Settlement</h3>
          <p className="text-[#567095] text-sm sm:text-base md:text-[24px] mt-2 sm:mt-4 text-center px-2">Enterprise settlement demo - instant, atomic, auditable</p>
        </div>
        
        
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {currentStep === 1 ? (
            // Step 1: Choose source and destination
            <div className="space-y-4 sm:space-y-6 bg-[#182233] rounded-lg px-4 sm:px-8 md:px-12 py-6 sm:py-8 md:py-12">
              <h4 className="text-lg sm:text-xl md:text-[24px] font-normal text-white">Choose your destination</h4>
              
              <div>
                {/* <label className="block text-[16px] font-normal text-gray-400 mb-2">
                  Company name
                </label> */}
                <div className="flex items-center space-x-2 sm:space-x-3 border-[1px] bg-[#182233] border-solid border-[#3C5475] rounded-md px-2 sm:px-3 py-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded flex items-center justify-center flex-shrink-0">
                    <img src={getStaticFileUrl(currentCompany?.logoUrl || '')} alt={currentCompany?.companyName || ''} className="w-6 h-6 sm:w-8 sm:h-8 rounded object-cover" />
                  </div>
                  <input
                    type="text"
                    value={currentCompany?.companyName || "Your Company"}
                    readOnly
                    className="flex-1 appearance-none rounded-md border-none bg-[#182233] px-2 sm:px-3 py-2 text-white text-sm sm:text-base md:text-[18px] truncate"
                  />
                </div>
              </div>
              
              <div>
                {/* <label className="block text-sm font-medium text-gray-400 mb-2">
                  Destination Company
                </label> */}
                <CompanyDropdown
                  companies={availableCompanies.map(c => c.company)}
                  selectedCompanyId={formData.receiverCompanyId}
                  onSelect={(companyId) => updateFormData({ receiverCompanyId: companyId })}
                  placeholder="Select Destination..."
                  disabled={isLoading}
                />
              </div>
              
              <button
                type="submit"
                disabled={!formData.receiverCompanyId || isLoading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 sm:py-3 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            // Step 2: Amount and governance
            <div className="space-y-4 sm:space-y-6">
              {/* Company cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-[#182233] border border-[#567095]/30 rounded-lg p-3 sm:p-4 flex flex-col items-center">
                  <div className="flex flex-col items-center bg-[#1D2838] rounded-lg px-3 sm:px-5 pt-5 sm:pt-7 pb-3 sm:pb-5 w-full sm:w-[190px]">
                    {currentCompany ? (
                      <CompanyLogo company={currentCompany} size={80} />
                    ) : (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-500 rounded flex items-center justify-center">
                        <span className="text-white text-lg sm:text-xl">∞</span>
                      </div>
                    )}
                    <span className="text-[#567095] text-sm sm:text-[20px] mt-2 font-[700] truncate w-full text-center">{currentCompany?.companyName || "Company name"}</span>
                  </div>
                  <div className="text-[#567095] text-sm sm:text-[16px] pt-2 sm:pt-3">Available Balance</div>
                  <div className="text-white text-lg sm:text-[26px] font-bold mt-1 sm:mt-2">
                    ${currentCompany ? (currentCompany.valueAmount / 100).toLocaleString() : "12,500,000"}
                  </div>
                </div>
                
                <div className="bg-[#182233] border border-[#567095]/30 rounded-lg p-3 sm:p-4 flex justify-center items-center">
                  <div className="flex items-center mb-3">
                    {formData.receiverCompanyId && (
                      <div className="flex flex-col items-center bg-[#1D2838] rounded-lg px-3 sm:px-5 pt-5 sm:pt-7 pb-3 sm:pb-5 w-full sm:w-[190px]">
                        {(() => {
                          const selectedCompany = availableCompanies.find(c => c.company.id === formData.receiverCompanyId)?.company;
                          return selectedCompany ? (
                            <CompanyLogo company={selectedCompany} size={80} />
                          ) : (
                            <div className="w-16 h-16 sm:w-[100px] sm:h-[100px] bg-red-500 rounded flex items-center justify-center">
                              <span className="text-white text-2xl sm:text-4xl">🎯</span>
                            </div>
                          );
                        })()}
                        <span className="text-[#567095] text-lg sm:text-[30px] font-[700] truncate w-full text-center">{formData.receiverCompanyId && availableCompanies.find(c => c.company.id === formData.receiverCompanyId)?.company.companyName || "Target"}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Amount input */}
              <div>
                {/* <label className="block text-sm font-medium text-gray-400 mb-2">
                  Amount
                </label> */}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#567095] text-base sm:text-[18px]">$</span>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => updateFormData({ amount: e.target.value })}
                    className="w-full pl-8 appearance-none rounded-md border-[1px] bg-[#182233] border-solid border-[#3C5475] px-3 py-2 sm:py-3 placeholder-[#567095] text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] text-base sm:text-[18px] text-center"
                    placeholder="Enter the value here..."
                    min="1"
                    step="1"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              
              {/* Transaction Information Panel */}
              {formData.amount && parseFloat(formData.amount) > 0 && formData.receiverCompanyId && (
                <div className="bg-[#182233] border border-[#567095]/30 rounded-lg p-3 sm:p-4">
                  <h5 className="text-white font-medium mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Transaction Details
                  </h5>
                  
                  <div className="space-y-2 sm:space-y-3">
                    {/* Exchange Rate */}
                    <div className="flex justify-between items-center">
                      <span className="text-[#567095] text-sm">Exchange Rate</span>
                      <span className="text-white text-sm font-medium">
                        {(() => {
                          const selectedCompany = availableCompanies.find(c => c.company.id === formData.receiverCompanyId);
                          return selectedCompany ? `1 : ${(selectedCompany.exchangeRate / 10000).toFixed(2)}` : 'N/A';
                        })()}
                      </span>
                    </div>
                    
                    {/* Protocol Fee */}
                    <div className="flex justify-between items-center">
                      <span className="text-[#567095] text-sm">Protocol Fee (0.08%)</span>
                      <span className="text-white text-sm font-medium">
                        ${(parseFloat(formData.amount) * 0.0008).toFixed(2)}
                      </span>
                    </div>
                    
                    {/* Amount to Receive */}
                    <div className="flex justify-between items-center">
                      <span className="text-[#567095] text-sm">Amount to Receive</span>
                      <span className="text-white text-sm font-medium">
                        {(() => {
                          const selectedCompany = availableCompanies.find(c => c.company.id === formData.receiverCompanyId);
                          if (selectedCompany) {
                            const amountToReceive = (parseFloat(formData.amount) * selectedCompany.exchangeRate) / 10000;
                            return `$${amountToReceive.toFixed(2)}`;
                          }
                          return 'N/A';
                        })()}
                      </span>
                    </div>
                    
                    {/* Max Settleable Amount */}
                    <div className="flex justify-between items-center border-t border-[#567095]/20 pt-3">
                      <span className="text-[#567095] text-sm">Max Settleable</span>
                      <span className="text-white text-sm font-medium">
                        {(() => {
                          if (!currentCompany) return 'N/A';
                          
                          const maxTxAmount = currentCompany.maxTransactionAmount / 100;
                          const availableBalance = currentCompany.valueAmount / 100;
                          const dailyRemaining = currentCompany.dailyLimit ? 
                            Math.max(0, (currentCompany.dailyLimit / 100) - (currentCompany.dailyUsed / 100)) : 
                            availableBalance;
                          
                          const maxSettleable = Math.min(maxTxAmount, availableBalance, dailyRemaining);
                          return `$${maxSettleable.toLocaleString()}`;
                        })()}
                      </span>
                    </div>
                    
                    {/* Daily Limit Info */}
                    {currentCompany && currentCompany.dailyLimit && (
                      <div className="flex justify-between items-center">
                        <span className="text-[#567095] text-xs">Daily Used</span>
                        <span className="text-[#567095] text-xs">
                          ${(currentCompany.dailyUsed / 100).toLocaleString()} / ${(currentCompany.dailyLimit / 100).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Governance Mode */}
              {(!formData.amount || parseFloat(formData.amount) < 1) && (<div className="bg-[#182233] border border-[#567095]/30 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h5 className="text-white font-medium">Governance Mode</h5>
                  {/* <button
                    type="button"
                    onClick={() => setGovernanceMode(!governanceMode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      governanceMode ? 'bg-[#2777EF]' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        governanceMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button> */}
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Settings className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-300 text-sm">Automated: For settlements under $50,000</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Eye className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-300 text-sm">Supervised: Requires one approval ($50k-$500k)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <BarChart3 className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-300 text-sm">Board Level: Requires multiple approvals (&gt;$500k)</span>
                  </div>
                </div>
              </div>)}
              {/* Fee Comparison */}
              {formData.amount && parseFloat(formData.amount) > 0 && (
                <div className="bg-[#182233] border border-[#567095]/30 rounded-lg p-6">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-[#FF6B35] text-[16px] font-medium">Traditional Time</div>
                      <div className="text-[#FF6B35] text-[20px] font-bold mt-1">3-7 Days</div>
                    </div>
                    <div>
                      <div className="text-[#FF6B35] text-[16px] font-medium">Traditional Fee</div>
                      <div className="text-[#FF6B35] text-[20px] font-bold mt-1">
                        ${(parseFloat(formData.amount) * 0.025).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[#2777EF] text-[16px] font-medium">LOOP Time</div>
                      <div className="text-[#2777EF] text-[20px] font-bold mt-1">~3 Sec</div>
                    </div>
                    <div>
                      <div className="text-[#2777EF] text-[16px] font-medium">LOOP Fee</div>
                      <div className="text-[#2777EF] text-[20px] font-bold mt-1">
                        ${(parseFloat(formData.amount) * 0.0008).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex justify-between space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
                  disabled={isLoading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2] disabled:opacity-50"
                  disabled={!formData.amount || isLoading}
                >
                  {isLoading ? 'Sending...' : 'Confirm Authorization'}
                </button>
              </div>
            </div>
          )}
        </form>
        
        <div className="mt-8 text-center text-gray-400 text-sm">
          © 2025 LoopChain
        </div>
      </div>

      {/* Transaction Processing Modal */}
      <TransactionProcessingModal
        // isOpen={showProcessing}
        transactionId={transData.transactionId}
        duration={transData.duration}
        amount={parseFloat(formData.amount) || 0}
        senderCompany={currentCompany?.companyName || "Your Company"}
        receiverCompany={availableCompanies.find(c => c.company.id === formData.receiverCompanyId)?.company.companyName || "Target"}
        onComplete={handleProcessingComplete}
        receiverCompanyWallet={availableCompanies.find(c => c.company.id === formData.receiverCompanyId)?.company.companyWallet}
        wallet={publicKey && signTransaction ? { publicKey, signTransaction } : undefined}
      />

      {/* Transaction Complete Modal */}
      <TransactionCompleteModal
        isOpen={showComplete}
        onClose={closeModal}
        onNewSettlement={handleNewSettlement}
        transactionData={transactionData}
        exchangeRate={availableCompanies.find(c => c.company.id === formData.receiverCompanyId)?.exchangeRate || 1}
      />
    </div>
  );
};

export default SendMoneyModal;
